/**
 * A widget opens as it was left (#74, the owner, 2026-10-24): its last params
 * (the open item, a view) and tab, **on this device only** — each browser and
 * each phone keeps its own, in its own storage, and nothing goes to the server.
 *
 * Only an open with nothing given is restored: a link or the assistant that
 * names what to show is obeyed. Storage can be missing or refuse (a private
 * window): then a widget opens fresh, as before.
 */
import type { Params } from "./layout";

const KEY = "kindred.widgets.last";

export interface Recalled {
  params: Params | null;
  tab: string | null;
}

function all(): Record<string, Recalled> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, Recalled>;
  } catch {
    return {};
  }
}

export function recall(widget: string): Recalled | null {
  return all()[widget] ?? null;
}

export function keep(widget: string, state: Recalled): void {
  try {
    const was = all();
    const now = was[widget];
    if (now && JSON.stringify(now) === JSON.stringify(state)) return;
    localStorage.setItem(KEY, JSON.stringify({ ...was, [widget]: state }));
  } catch {
    // Storage refused: the widget opens fresh next time.
  }
}

/** For a fold of a widget's own (Notes' notebooks): a set, kept by name. */
export function keptSet(name: string): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(`${KEY}.${name}`) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

export function keepSet(name: string, value: Set<string>): void {
  try {
    localStorage.setItem(`${KEY}.${name}`, JSON.stringify([...value]));
  } catch {
    // As above.
  }
}
